import * as React from 'react';

import { cn, Col } from '@rematter/ui-web';
import { LucideBriefcase, LucideFileText, LucidePackage } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

interface PromptCardProps {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
}

//
// ⚛️ ⚛️ ⚛️ PROMPT CARD ⚛️ ⚛️ ⚛️
//
const PromptCard = ({ icon, text, onClick }: PromptCardProps) => (
  <button
    onClick={onClick}
    className={cn(
      '@PromptCard',
      'group relative flex flex-col justify-between rounded-lg border border-neutral/20',
      'bg-white p-3 text-left transition-all hover:border-neutral hover:shadow-md',
    )}
  >
    <p className="mb-4 text-xs leading-relaxed text-neutral/80">{text}</p>
    <div
      className={cn(
        'flex h-8 w-8 items-center justify-center self-start rounded-lg',
        'bg-neutral text-gray transition-colors',
        'group-hover:bg-neutral group-hover:text-white',
      )}
    >
      {icon}
    </div>
  </button>
);

interface PromptListProps {
  onSelectPrompt: (prompt: string) => void;
}

//
// ⚛️ ⚛️ ⚛️ PROMPT LIST ⚛️ ⚛️ ⚛️
//
export const PromptList = ({ onSelectPrompt }: PromptListProps) => {
  const t = useTranslations();

  // Memoized values
  const prompts = React.useMemo(
    () => [
      {
        icon: <LucideBriefcase size={16} />,
        text: t.prompts.suggestions.createJob,
      },
      {
        icon: <LucideFileText size={16} />,
        text: t.prompts.suggestions.createSalesOrder,
      },
      {
        icon: <LucidePackage size={16} />,
        text: t.prompts.suggestions.showInventory,
      },
    ],
    [t],
  );

  return (
    <Col xCenter yCenter className="@PromptList p-4">
      <Col className="max-w-4xl">
        <Col xCenter className="mb-8 text-center">
          <h1
            className={cn(
              'mb-2 bg-linear-to-r from-[#071429] via-[#2C1A38] via-[#B64D9E] to-[#5F4AD9]',
              'bg-clip-text text-4xl font-bold text-transparent',
            )}
          >
            {t.prompts.title}
          </h1>
          <p className="text-neutral/60">{t.prompts.subtitle}</p>
        </Col>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {prompts.map((prompt, index) => (
            <PromptCard
              key={index}
              icon={prompt.icon}
              text={prompt.text}
              onClick={() => onSelectPrompt(prompt.text)}
            />
          ))}
        </div>
      </Col>
    </Col>
  );
};
